import EyeIcon from "@/assets/eye.svg";
import DotsIcon from "@/assets/placeholder-dots.svg";
import { ArrowRight } from "lucide-react";
import { CircleFlag } from "react-circle-flags";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import type { RouterOutputs } from "@/lib/trpc";

import { useTranslationUtils } from "@/hooks/use-translation-utils";
import { cn, formatDate, formatYear } from "@/lib/utils";
import { routes } from "@/routes";

import { Button } from "../ui/button";

export function ClubTournamentCard({ tournaments }: { tournaments: RouterOutputs["club"]["getClub"]["tournaments"] }) {
  const navigate = useNavigate();
  const { getCountryName } = useTranslationUtils();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const { t } = useTranslation(["common", "tournaments", "profile"]);

  return (
    <>
      <h3 className="p-2 font-semibold leading-[1.1] text-neutral-500">
        {t("common:desktop-nav.tournaments")}
      </h3>

      <div className="flex flex-col gap-2 rounded-[20px] bg-white p-2">
        {tournaments.length > 0
          ? (
              tournaments.map(tournament => (
                <div
                  key={tournament.id}
                  className="flex flex-col gap-2 rounded-xl border p-2"
                >
                  <div
                    className="flex cursor-pointer items-center gap-3"
                    onClick={() => navigate(routes.getTournamentPath(tournament.id), { state: { fromPath: routes.profile } })}
                  >
                    <img
                      src={tournament.imgUrl}
                      alt={`Profil de ${tournament.name}`}
                      className="pointer-events-none size-14 shrink-0 select-none rounded-xl object-cover"
                    />

                    <div className="flex min-w-0 flex-col gap-1">
                      <h1 className="truncate text-lg font-semibold uppercase">{tournament.name}</h1>

                      <div className="flex w-fit min-w-0 items-center gap-1.5 rounded-full bg-neutral-100 p-0.5">
                        <CircleFlag countryCode={tournament.country.toLowerCase()} className="size-4 rounded-full" />
                        <span className="block truncate pr-3 text-sm font-medium">
                          {`${tournament.city}, ${getCountryName(tournament.country)}`}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-2">
                    {tournament.tournamentSurface
                      ? (
                          <span className={cn(
                            "text-nowrap rounded-md px-1.5 py-2 text-center text-sm font-medium leading-4",
                            tournament.tournamentSurface?.includes("clay") && "bg-orange-300",
                            tournament.tournamentSurface?.includes("hard") && "bg-blue-200",
                          )}
                          >
                            { t(`profile:custom-surface.${tournament.tournamentSurface}`)}
                          </span>
                        )
                      : (
                          <span className="pl-2 text-center font-medium">-</span>
                        )}

                    {tournament.winnerPrize
                      ? (
                          <div className="flex flex-col">
                            <span className="block text-sm">
                              {t("tournaments:winner-prize")}
                            </span>
                            <span className="block text-nowrap text-end text-sm font-semibold">
                              {`${tournament.winnerPrize?.toLocaleString()} €`}
                            </span>
                          </div>
                        )
                      : (
                          <span className="pr-2 text-center font-medium">-</span>
                        )}
                  </div>

                  <div className="flex items-center gap-2 rounded-xl border bg-white py-2">
                    <div className="flex w-full flex-col items-center text-nowrap py-2 leading-5">
                      <span className="font-semibold">
                        {formatDate(tournament.startDate, locale)}
                      </span>
                      <span className="font-medium text-neutral-500">{formatYear(tournament.startDate, locale)}</span>
                    </div>

                    <ArrowRight className="size-6 shrink-0 text-orange-500" />

                    <div className="flex w-full flex-col items-center text-nowrap py-2 leading-5">
                      <span className="font-semibold">
                        {formatDate(tournament.endDate, locale)}
                      </span>
                      <span className="font-medium text-neutral-500">{formatYear(tournament.endDate, locale)}</span>
                    </div>
                  </div>

                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => navigate(routes.getTournamentPath(tournament.id), { state: { fromPath: routes.profile } })}
                  >
                    <EyeIcon />
                    <span>
                      {t("common:go-to-tournament-page")}
                    </span>
                  </Button>
                </div>
              ),
              )
            )
          : (
              <span
                className="flex h-14 items-center justify-center rounded-xl border bg-neutral-50 p-4 text-neutral-600"
              >
                <DotsIcon />
              </span>
            )}
      </div>
    </>
  );
}
